import {
    Card, Title, Select, Group, Button, Paper, Stack, Text, Modal
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import AddressForm, { AddressFormValues } from '../forms/AddressForm';
import { Address } from '../../types/shipping';

interface AddressSectionProps {
    addresses: Address[];
    selectedAddressId: string;
    onAddressChange: (addressId: string | null) => void;
    setAddresses: React.Dispatch<React.SetStateAction<Address[]>>;
    isLoading: boolean;
}

const AddressSection = ({
    addresses,
    selectedAddressId,
    onAddressChange,
    setAddresses,
    isLoading
}: AddressSectionProps) => {
    const [opened, { open, close }] = useDisclosure(false);
    const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);

    const handleAddAddress = (values: AddressFormValues) => {
        // Make sure we have a valid ID
        if (!values.id) {
            console.error('Address ID is missing in the form values');
            return;
        }

        // Create a new address with an ID
        const newAddress: Address = {
            id: values.id.toString(),
            name: values.address.split('\n')[0] || 'Unnamed', // Use first line of address as name
            street: values.address,
            city: values.city,
            state: values.state,
            postalCode: values.zip,
            country: values.country,
            phone: '', // Required by the Address interface but not part of the form
        };

        // Add new address to the list
        const updatedAddresses = [...addresses, newAddress];
        setAddresses(updatedAddresses);

        // Select the new address
        onAddressChange(newAddress.id || '');
        close();
    };

    return (
        <>
            <Modal opened={opened} onClose={close} title="Add New Address" size="lg">
                <AddressForm onSubmit={handleAddAddress} isLoading={isLoading} />
            </Modal>

            <Card withBorder shadow="sm" radius="md" p="lg" mb="lg">
                <Title order={2} size="h3" mb="md">Shipping Address</Title>

                <Select
                    label="Select shipping address"
                    placeholder="Choose an address"
                    data={addresses.map((address) => ({
                        value: String(address.id),
                        label: `${address.street} - ${address.state}, ${address.country}`
                    }))}
                    value={selectedAddressId}
                    onChange={(value) => onAddressChange(value)}
                    mb="md"
                    required
                />

                <Group mb="md">
                    <Button variant="subtle" onClick={open} leftSection={<IconPlus size={16} />}>
                        Add New Address
                    </Button>
                </Group>

                {selectedAddress && (
                    <Paper withBorder p="md" radius="md">
                        <Stack>
                            <Text fw={700}>{selectedAddress.name}</Text>
                            <Text>{selectedAddress.street}</Text>
                            <Text>{selectedAddress.city}, {selectedAddress.state} {selectedAddress.postalCode}</Text>
                            <Text>{selectedAddress.country}</Text>
                        </Stack>
                    </Paper>
                )}
            </Card>
        </>
    );
};

export default AddressSection;
